import z from 'zod';

import { ErpActionInteractionSchema } from '../erp';

const AssistantActionInputFieldSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('string'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), defaultValue: z.string().max(4_000).optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional(), binding: z.literal('current-user-message').optional() }).strict(),
  z.object({ type: z.literal('number'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), defaultValue: z.number().optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional() }).strict(),
  z.object({ type: z.literal('boolean'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), defaultValue: z.boolean().optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional() }).strict(),
  z.object({ type: z.literal('date'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), defaultValue: z.string().max(100).optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional() }).strict(),
  z.object({ type: z.literal('textarea'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), defaultValue: z.string().max(16_000).optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional(), binding: z.literal('current-user-message').optional() }).strict(),
  z.object({ type: z.literal('select'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), options: z.array(z.object({ value: z.string().max(200), label: z.string().max(200) }).strict()).max(100), defaultValue: z.string().max(200).optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional() }).strict(),
  z.object({ type: z.literal('multiselect'), name: z.string().trim().min(1).max(160), label: z.string().max(200), description: z.string().max(2_000).optional(), required: z.boolean().optional(), options: z.array(z.object({ value: z.string().max(200), label: z.string().max(200) }).strict()).max(100), defaultValue: z.array(z.string().max(200)).max(100).optional(), purpose: z.enum(['value', 'instruction', 'evidence']).optional() }).strict(),
]);

export const AssistantActionInputSchemaSchema = z.object({
  fields: z.array(AssistantActionInputFieldSchema).max(100),
}).strict();

const AssistantActionRequirementSchema = z.object({
  label: z.string().max(200),
  required: z.boolean(),
  cardinality: z.enum(['one', 'many']),
  acceptedTypes: z.array(z.string().trim().min(1).max(80)).max(32),
}).strict();

const AssistantActionAttachmentsSchema = z.record(
  z.string().trim().min(1).max(80),
  z.object({
    label: z.string().max(200),
    required: z.boolean(),
    cardinality: z.enum(['one', 'many']).optional(),
    acceptedMimeTypes: z.array(z.string().trim().min(1).max(160)).max(32).optional(),
    acceptedExtensions: z.array(z.string().trim().min(1).max(32)).max(32).optional(),
    semanticTypes: z.record(
      z.string().trim().min(1).max(80),
      z.object({
        label: z.string().max(200),
        filenameHints: z.array(z.string().trim().min(1).max(160)).max(32).optional(),
      }).strict(),
    ).optional(),
  }).strict(),
).refine((attachments) => Object.keys(attachments).length <= 32, 'Too many attachment requirements');

const AssistantActionAttachmentMatchSchema = z.object({
  slot: z.string().trim().min(1).max(80),
  cardinality: z.enum(['one', 'many']),
  sourceIds: z.array(z.string().trim().min(1).max(256)).max(32),
  resolvedSemanticTypes: z.array(z.object({
    sourceId: z.string().trim().min(1).max(256),
    semanticType: z.string().trim().min(1).max(80),
  }).strict()).max(32).optional(),
}).strict();

export const AssistantActionCapabilitySchema = z.object({
  id: z.string().trim().min(1).max(160),
  label: z.string().max(200).optional(),
  description: z.string().max(4_000).optional(),
  inputSchema: AssistantActionInputSchemaSchema.optional(),
  targets: z.record(z.string().trim().min(1).max(80), AssistantActionRequirementSchema).refine((targets) => Object.keys(targets).length <= 32, 'Too many target requirements').optional(),
  attachments: AssistantActionAttachmentsSchema.optional(),
  interaction: ErpActionInteractionSchema.optional(),
  relevance: z.enum(['contextual', 'global', 'partial', 'unresolved']),
  matchedTargetRoles: z.array(z.string().trim().min(1).max(80)).max(32),
  missingTargetRoles: z.array(z.string().trim().min(1).max(80)).max(32),
  matchedAttachmentSlots: z.array(z.string().trim().min(1).max(80)).max(32).optional(),
  matchedSourceIds: z.array(z.string().trim().min(1).max(256)).max(32).optional(),
  attachmentMatches: z.array(AssistantActionAttachmentMatchSchema).max(32).optional(),
}).strict();

export type AssistantActionCapability = z.infer<typeof AssistantActionCapabilitySchema>;

export const AssistantCapabilitiesSchema = z.object({
  actions: z.array(AssistantActionCapabilitySchema).max(100),
  status: z.enum(['available', 'unavailable']).default('available'),
}).strict();

export type AssistantCapabilities = z.infer<typeof AssistantCapabilitiesSchema>;
